import firebase from "firebase/compat/app"; // Import compat for Auth providers
import { db, auth, getSecondaryApp } from './firebase'; // Removed 'storage' import
import { 
  collection, getDocs, doc, setDoc, getDoc, deleteDoc, query, where, limit, writeBatch, updateDoc
} from "firebase/firestore";

import { User, UserRole, SurveyData, CardTemplate, DynamicQuestion, SurveySection } from '../types';
import { INITIAL_QUESTIONS, INITIAL_SECTIONS } from '../constants';

const SURVEYS_COLLECTION = 'surveys';
const USERS_COLLECTION = 'users';
const SETTINGS_COLLECTION = 'settings';

// Helper to construct email from username
export const getEmail = (username: string) => {
  const clean = username.trim();
  if (clean.includes('@')) return clean;
  return `${clean.toLowerCase().replace(/\s/g, '')}@mahallu.app`;
};

export const initStorage = async () => {
  // CRITICAL: If no user is logged in, Firestore rules will likely block this.
  // We check for currentUser. If null, we skip initialization.
  // It will be called again after login in App.tsx.
  if (!auth.currentUser) {
    console.log("Skipping storage initialization (Not logged in)");
    return;
  }

  try {
    // 1. Check Questions
    const questionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
    if (!questionsDoc.exists()) {
      await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: INITIAL_QUESTIONS });
    } else {
      // Check for new mandatory fields for the new survey structure
      let currentList = questionsDoc.data().list as DynamicQuestion[];
      // Essential fields for the new requirements
      const MANDATORY_IDS = ['serialNo', 'familyBranch', 'headName', 'parentName'];
      
      let needsUpdate = false;
      
      // 1. Check Mandatory IDs
      MANDATORY_IDS.forEach(id => {
        const exists = currentList.find(q => q.id === id);
        if (!exists) {
          const original = INITIAL_QUESTIONS.find(q => q.id === id);
          if (original) {
            currentList.push(original);
            needsUpdate = true;
          }
        }
      });
      
      // Ensure Serial No is in Section A (Migrate if old structure exists)
      const serialQ = currentList.find(q => q.id === 'serialNo');
      if (serialQ && serialQ.section !== 'sectionA') {
          serialQ.section = 'sectionA';
          needsUpdate = true;
      }

      if (needsUpdate) {
        console.log("Updating questions schema...");
        await updateDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: currentList });
      }
    }

    // 2. Check Sections
    const sectionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    if (!sectionsDoc.exists()) {
      await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: INITIAL_SECTIONS });
    }
  } catch (error: any) {
    // Gracefully handle permission errors if rules are strict
    if (error.code === 'permission-denied') {
      console.warn("Storage initialization skipped due to permissions. (Normal if basic user)");
    } else {
      console.warn("Storage initialization warning:", error);
    }
  }
};

// --- Authentication ---

export const registerMasterAdmin = async (name: string, username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("Failed to retrieve User ID from Firebase.");

    const user: User = {
      id: uid,
      username,
      name,
      role: UserRole.MASTER_ADMIN,
      visibleSections: ['sectionA', 'sectionB']
    };

    await setDoc(doc(db, USERS_COLLECTION, uid), user);
    return user;
  } catch (error: any) {
    console.error("Registration Error:", error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error(`The account '${email}' already exists.`);
    }
    if (error.code === 'auth/weak-password') {
      throw new Error("Password should be at least 6 characters.");
    }
    throw new Error(error.message || "Failed to register Master Admin.");
  }
};

export const loginUser = async (username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.signInWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("User ID not found after login");

    try {
      const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
      if (userDoc.exists()) {
        return { id: uid, ...userDoc.data() } as User;
      } else {
        throw new Error("Account exists in Auth, but user profile is missing in Database.");
      }
    } catch (dbError: any) {
      if (dbError.message && dbError.message.includes("offline")) {
        throw new Error("Login successful, but cannot fetch profile (Offline). Check internet connection.");
      }
      throw dbError;
    }
  } catch (error: any) {
    const msg = error.message || '';
    if (msg.includes('auth/invalid-credential') || error.code === 'auth/wrong-password') {
      throw new Error(`Invalid credentials. Check username/password.`);
    }
    if (error.code === 'auth/network-request-failed') {
      throw new Error("Network error. Please check your connection.");
    }
    throw new Error(msg || "Login failed due to an unknown error.");
  }
};

export const logoutUser = async () => {
  await auth.signOut();
};

export const verifyUserCredentials = async (username: string, password: string) => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    throw new Error("No user is currently logged in.");
  }

  const inputEmail = getEmail(username);
  
  if (inputEmail.toLowerCase() !== currentUser.email.toLowerCase()) {
    throw new Error("Username does not match the currently logged-in user.");
  }

  try {
    const credential = firebase.auth.EmailAuthProvider.credential(currentUser.email, password);
    await currentUser.reauthenticateWithCredential(credential);
    return true;
  } catch (error: any) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error("Incorrect password.");
    }
    throw error;
  }
};

// --- Users Management ---

export const getUsers = async (): Promise<User[]> => {
  try {
    const snapshot = await getDocs(collection(db, USERS_COLLECTION));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
  } catch (error) {
    console.error("Failed to fetch users:", error);
    return [];
  }
};

export const saveUser = async (user: Omit<User, 'id'>, password: string) => {
  const secondaryApp = getSecondaryApp();
  const secondaryAuth = secondaryApp.auth();
  
  try {
    const email = getEmail(user.username);
    const userCredential = await secondaryAuth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;

    if (!uid) throw new Error("Failed to generate UID");

    await setDoc(doc(db, USERS_COLLECTION, uid), {
      username: user.username,
      name: user.name,
      role: user.role,
      visibleSections: user.visibleSections || ['sectionA', 'sectionB']
    });

    await secondaryAuth.signOut();
    await secondaryApp.delete();
  } catch (error: any) {
    console.error("Create User Error:", error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error("Username/Email already exists.");
    }
    throw new Error(error.message);
  }
};

export const updateUser = async (userId: string, data: Partial<User>) => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, userId), data);
  } catch (e: any) {
    throw new Error("Failed to update user profile: " + e.message);
  }
};

export const updateCurrentUserAuth = async (password: string) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  if (password) await user.updatePassword(password);
};

export const deleteUser = async (userId: string) => {
  await deleteDoc(doc(db, USERS_COLLECTION, userId));
};

// --- Surveys ---

export const getSurveys = async (filters?: { userId?: string, limitCount?: number }): Promise<SurveyData[]> => {
  try {
    let q = query(collection(db, SURVEYS_COLLECTION));

    if (filters?.userId) {
      q = query(q, where('submittedBy', '==', filters.userId));
    }

    if (filters?.limitCount) {
      q = query(q, limit(filters.limitCount));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as SurveyData);
  } catch (error) {
    console.error("Failed to fetch surveys:", error);
    return [];
  }
};

export const saveSurvey = async (survey: SurveyData) => {
  await setDoc(doc(db, SURVEYS_COLLECTION, survey.id), survey);
};

export const deleteSurvey = async (id: string) => {
  await deleteDoc(doc(db, SURVEYS_COLLECTION, id));
};

export const resetSurveys = async () => {
  const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
  const batch = writeBatch(db);
  snapshot.docs.forEach((doc) => {
    batch.delete(doc.ref);
  });
  await batch.commit();
};

export const getNextSerialNo = async (): Promise<string> => {
  const surveys = await getSurveys(); 
  if (surveys.length === 0) return '0001';
  
  let max = 0;
  surveys.forEach(s => {
    const num = parseInt(s.sectionA?.serialNo || '0');
    if (!isNaN(num) && num > max) max = num;
  });
  
  return (max + 1).toString().padStart(4, '0');
};

// --- Settings (Questions & Sections) ---

export const getDynamicSections = async (): Promise<SurveySection[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    if (docSnap.exists()) {
      return (docSnap.data().list as SurveySection[]).sort((a,b) => a.order - b.order);
    }
  } catch (error) {
    console.warn("Error fetching sections:", error);
  }
  return INITIAL_SECTIONS;
}

export const saveSections = async (sections: SurveySection[]) => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: sections });
};

export const getDynamicQuestions = async (section?: string): Promise<DynamicQuestion[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
    if (docSnap.exists()) {
      const all = docSnap.data().list as DynamicQuestion[];
      if (section) return all.filter(q => q.section === section);
      return all;
    }
  } catch (error) {
    // console.warn("Error fetching questions (likely permissions):", error);
    // Don't warn loudly, just return defaults.
  }
  return section ? INITIAL_QUESTIONS.filter(q => q.section === section) : INITIAL_QUESTIONS;
};

export const saveDynamicQuestion = async (q: DynamicQuestion) => {
  const current = await getDynamicQuestions();
  const index = current.findIndex(item => item.id === q.id);
  
  let newList = [...current];
  if (index >= 0) {
    newList[index] = q;
  } else {
    newList.push(q);
  }
  
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveQuestionsOrder = async (questions: DynamicQuestion[]) => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: questions });
};

export const deleteDynamicQuestion = async (id: string) => {
  const current = await getDynamicQuestions();
  const newList = current.filter(q => q.id !== id);
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveTemplate = async (template: CardTemplate) => {
  // We now save the Base64 string directly to the document
  // Firestore limit is 1MB, so image must be compressed client-side before calling this
  await setDoc(doc(db, SETTINGS_COLLECTION, 'template'), template);
};

export const getTemplate = async (): Promise<CardTemplate | null> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template'));
    return docSnap.exists() ? docSnap.data() as CardTemplate : null;
  } catch (e) {
    console.warn("Failed to get template:", e);
    return null;
  }
};